import { useState, useEffect, ReactNode, useMemo } from 'react';
import { getAdminLabels } from '@/lib/adminApi';
import { useRBAC } from './RBACContext';
import {
  SettingsContext,
  loadSystemConfig,
  saveSystemConfig,
  DEFAULT_SYSTEM_CONFIG,
  SYSTEM_CONFIG_STORAGE_KEY,
  type IndustryLabel,
  type SystemConfig,
  type AdminUser,
  type AdminCompany,
  type AdminMembership,
  type SettingsContextType,
} from './SettingsContext';

export function SettingsProvider({ children }: { children: ReactNode }) {
  // Get RBAC data from RBACContext (single source of truth)
  const {
    user: rbacUser,
    companies: rbacCompanies,
    memberships: rbacMemberships,
    permissions: rbacPermissions,
    capabilities: rbacCapabilities,
    isSuperadmin,
    loading: rbacLoading,
    refreshRBAC,
  } = useRBAC();

  // Settings-specific state only
  const [labels, setLabels] = useState<IndustryLabel[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig>(loadSystemConfig);
  const [labelsLoading, setLabelsLoading] = useState(false);

  // Update system config and persist to localStorage
  const updateSystemConfig = (updates: Partial<SystemConfig>) => {
    setSystemConfig((prev) => {
      const newConfig = { ...prev, ...updates };
      saveSystemConfig(newConfig);
      return newConfig;
    });
  };

  const loadLabels = async () => {
    // Default labels to use as fallback
    const defaultLabels: IndustryLabel[] = [
      {
        id: '1',
        industry: 'film',
        label_type: 'group',
        label_value: 'Production',
        icon: null,
        superadmin_override: false,
        admin_override: false,
      },
      {
        id: '2',
        industry: 'default',
        label_type: 'group',
        label_value: 'Site',
        icon: null,
        superadmin_override: false,
        admin_override: false,
      },
    ];

    setLabelsLoading(true);
    try {
      const response = await getAdminLabels();
      // If we get labels from API, use them
      if (response.labels && response.labels.length > 0) {
        setLabels(response.labels);
        return;
      }
    } catch (error) {
      // Silently handle errors - labels endpoint might not exist yet or backend might be down
      // This is expected during development before migration is run
      if (process.env.NODE_ENV === 'development') {
        // Only log in development for debugging
        console.warn(
          'Labels API not available, using defaults:',
          error instanceof Error ? error.message : error
        );
      }
    } finally {
      setLabelsLoading(false);
    }

    // Always set default labels if API fails or returns empty
    setLabels(defaultLabels);
  };

  // Load labels once when RBAC is ready
  useEffect(() => {
    if (!rbacLoading && rbacUser) {
      // Don't await - let it load in background, won't block settings context
      loadLabels().catch(() => {
        // Silently handle - loadLabels already sets defaults on error
      });
    }
  }, [rbacLoading, rbacUser]);

  // Map RBAC user to AdminUser interface
  const user: AdminUser | null = rbacUser
    ? {
        id: rbacUser.id,
        email: rbacUser.email,
        first_name: rbacUser.first_name,
        last_name: rbacUser.last_name,
        is_internal: rbacUser.is_internal,
        default_company_id: rbacUser.default_company_id,
        default_group_id: rbacUser.default_group_id,
      }
    : null;

  // Map RBAC companies to AdminCompany interface for backward compatibility
  const companies: AdminCompany[] = useMemo(
    () =>
      rbacCompanies.map((a) => ({
        id: a.id,
        name: a.name,
        slug: a.slug,
        is_internal: a.is_internal,
        role: a.role,
        industry: a.industry,
        logo_url: a.logo_url || null,
        theme_json: a.theme_json || null,
      })),
    [rbacCompanies]
  );

  // Map RBAC memberships to AdminMembership interface for backward compatibility
  const memberships: AdminMembership[] = useMemo(
    () =>
      rbacMemberships.map((m) => ({
        id: m.id,
        company_id: m.company_id,
        group_id: m.group_id,
        role: m.role,
        company_name: m.company_name,
        group_name: m.group_name,
      })),
    [rbacMemberships]
  );

  // Listen for storage events to sync systemConfig across tabs
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === SYSTEM_CONFIG_STORAGE_KEY && e.newValue) {
        try {
          setSystemConfig({ ...DEFAULT_SYSTEM_CONFIG, ...JSON.parse(e.newValue) });
        } catch {
          // Ignore parse errors
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const contextValue = useMemo<SettingsContextType>(
    () => ({
      // RBAC data (from RBACContext)
      user,
      companies,
      memberships,
      permissions: rbacPermissions,
      capabilities: rbacCapabilities,
      is_superadmin: isSuperadmin,

      // Settings-specific data
      labels,
      systemConfig,
      loading: rbacLoading || labelsLoading,

      // Actions
      refresh: refreshRBAC,
      refreshLabels: loadLabels,
      updateSystemConfig,
    }),
    [
      user,
      companies,
      memberships,
      rbacPermissions,
      rbacCapabilities,
      isSuperadmin,
      labels,
      systemConfig,
      rbacLoading,
      labelsLoading,
      refreshRBAC,
    ]
  );

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
}
